import { Dispatch, FC, SetStateAction, useContext, useEffect, useState } from 'react'
import { Box, Button, Checkbox, MenuItem, Select, TextField, Typography } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import BookmarkIcon from '@mui/icons-material/Bookmark'
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder'
import { RequestContext } from '../../../../contexts/request.context'
import {
  updateFrequencyGroup,
  createFrequencyGroup,
  getIntermediateFunctionsByMajorFunctionId,
  deleteFrequencyGroup,
  getAllFrequencyExclusions,
  getAllFrequencyTemplates,
  deleteFrequencyExclusion,
  createFrequencyExclusion,
  updateFrequencyExclusion,
  getRequestFrequencyGroupReceiveLocations,
  saveRequestFrequencyGroupReceiveLocations,
} from '../../../../services/request-form.service'
import SectionLayout from '../SectionLayout'
import {
  IFrequencyTemplate,
  IIntermediateFunction,
  IRequestFrequencyGroup,
  IRequestFrequencyExclusion,
  IRequestFrequencyGroupReceiveLocation,
} from '../../../../types/exercise.type'
import { useToast } from '../../../../contexts/toast.context'
import { SelectComponent } from '../../../shared/SelectComponent'
import { SelectWithSearch } from '../../../shared/SearchSelect'
import {
  createFrequencyForGroup,
  getFrequenciesByFrequencyGroupId,
} from '../../../../services/spectrum-manager.service'
import { isNotNull, calculateAzimuth, isFrequencyValid } from '../../../../utils/functions'

import styles from '../SectionLayout/styles.module.scss'

interface IProps {
  setValue: Dispatch<SetStateAction<number>>
  isLightMode: boolean
}

const FrequencySection: FC<IProps> = ({ isLightMode, setValue }) => {
  const [frequency, setFrequency] = useState<IRequestFrequencyGroup | null>(null)
  const [actualNumberOfFrequencies, setActualNumberOfFrequencies] = useState(0)
  const [isDelete, setIsDelete] = useState(false)
  const [frequencyTemplates, setFrequencyTemplates] = useState<IFrequencyTemplate[]>([])
  const [frequencyExclusions, setFrequencyExclusions] = useState<IRequestFrequencyExclusion[]>([])
  const [intermediateFunctions, setIntermediateFunctions] = useState<IIntermediateFunction[]>([])
  const [receiveLocations, setReceiveLocations] = useState<IRequestFrequencyGroupReceiveLocation[]>([])

  const {
    usageTimes,
    majorFunctions,
    usageTimeServices,
    selectedRequest,
    requestLocations,
    frequencyGroups,
    setFrequencyGroups,
    getRequestFrequencies,
    selectedFrequencyGroup,
    setSelectedFrequencyGroup,
  } = useContext(RequestContext)

  const { showToast } = useToast()

  const handleClose = () => {
    setIsDelete(false)
  }

  const handleAdd = async () => {
    if (selectedRequest) {
      const body = await createFrequencyGroup(selectedRequest.id)

      if (body.success && body.data) {
        setFrequencyGroups([...frequencyGroups, body.data])
        showToast('success', 'Frequency created successfully')

        const newFreq = await createFrequencyForGroup(body.data.id)

        if (newFreq.success) {
          setActualNumberOfFrequencies(1)
        }
      }
    }
  }

  const handleSave = async () => {
    if (!isFrequencyValid(frequency)) {
      showToast('error', 'Please choose the frequency band')
      return
    }

    if (frequency) {
      const data = await updateFrequencyGroup(frequency.id, {
        hopset: '',
        saturn: false,
        platform: frequency.platform || '',
        locationId: frequency.request_location_id,
        usageTimeId: frequency.usage_time_id || null,
        percentTime: 0,
        majorFunctionId: frequency.major_function_id || null,
        usageTimeServiceId: frequency.usage_time_service_id || null,
        detailedFunction: frequency.detailed_function || null,
        numberOfFrequencies: frequency.number_of_frequencies,
        supplementaryDetails: frequency.supplementary_details,
        intermediateFunctionId: frequency.intermediate_function_id || null,
        startFreq: frequency.start_freq,
        startIndicator: frequency.start_indicator,
        endFreq: frequency.end_freq,
        endIndicator: frequency.end_indicator,
        isCustom: frequency.is_custom,
        frequencyTemplateId: frequency.frequency_template_id,
      })

      if (data.success && data.data) {
        showToast('success', 'Frequency updated successfully')
        getRequestFrequencies()

        // Save the receive locations

        await saveRequestFrequencyGroupReceiveLocations(
          frequency.id,
          receiveLocations.map((rl) => rl.request_location_id).filter(isNotNull),
        )

        for (const excl of frequencyExclusions) {
          await updateFrequencyExclusion(excl.id, {
            start_indicator: excl.start_indicator,
            start_freq: excl.start_freq,
            end_indicator: excl.end_indicator,
            end_freq: excl.end_freq,
            is_band: excl.is_band,
          })
        }

        // Determine the difference between the specified number of frequencies and the existing frequencies
        const freqDiff = frequency?.number_of_frequencies - actualNumberOfFrequencies

        for (let i = 0; i < freqDiff; i++) {
          await createFrequencyForGroup(frequency.id)
        }

        getRequestFrequencies()
      }
    }

    handleClose()
  }

  const handleFrequencyChange = (key: string, value: string | number | boolean | null) => {
    if (frequency) {
      setFrequency({ ...frequency, [key]: value })
    }
  }

  const handleDelete = async () => {
    if (selectedFrequencyGroup) {
      const data = await deleteFrequencyGroup(selectedFrequencyGroup.id)

      if (data.success) {
        setFrequencyGroups(frequencyGroups.filter((frequency) => frequency.id !== selectedFrequencyGroup.id))
        showToast('success', 'Frequency removed successfully')

        setSelectedFrequencyGroup(null)
      }
    }

    handleClose()
  }

  const getIntermediateFunctions = async () => {
    if (frequency?.major_function_id) {
      const data = await getIntermediateFunctionsByMajorFunctionId(frequency.major_function_id)

      if (data.success && data.data) {
        setIntermediateFunctions(data.data)
      }
    }
  }

  const getFrequencyTemplates = async () => {
    const data = await getAllFrequencyTemplates()

    if (data.success && data.data) {
      setFrequencyTemplates(data.data)
    }
  }

  const getSingleFrequencies = async () => {
    if (selectedFrequencyGroup) {
      const data = await getAllFrequencyExclusions(selectedFrequencyGroup.id)

      if (data.data && data.success) {
        setFrequencyExclusions(data.data)
      }
    }
  }

  const refreshRequestFrequencyGroupReceiveLocations = async () => {
    if (!selectedFrequencyGroup) return

    const body = await getRequestFrequencyGroupReceiveLocations(selectedFrequencyGroup.id)
    setReceiveLocations(body.data || [])
  }

  const handleAddReceiveLocation = async () => {
    if (!selectedFrequencyGroup) return

    setReceiveLocations((prev) => [
      ...prev,
      {
        id: null,
        request_frequency_group_id: selectedFrequencyGroup.id,
        request_location_id: null,
      },
    ])
  }

  const handleChooseReceiveLocation = async (i: number, request_location_id: number) => {
    setReceiveLocations((prev) => [
      ...prev.slice(0, i),
      {
        ...prev[i],
        request_location_id,
      },
      ...prev.slice(i + 1, prev.length),
    ])
  }

  const handleDeleteReceiveLocation = async (i: number) => {
    setReceiveLocations((prev) => prev.filter((_, j) => j !== i))
  }

  const handleAddSingleFrequency = async () => {
    if (frequency) {
      const data = await createFrequencyExclusion(frequency.id)

      if (data.data && data.success) {
        setFrequencyExclusions([...frequencyExclusions, data.data])
      }
    }
  }

  const handleSingleFrequencyDelete = async (id: number) => {
    const data = await deleteFrequencyExclusion(id)

    if (data.success) {
      setFrequencyExclusions(frequencyExclusions.filter((excl) => excl.id !== id))
    }
  }

  const handleFrequencyExclusionUpdate = async (id: number, key: string, value: string | number | boolean) => {
    const updatedExclusions = frequencyExclusions.map((excl) => (excl.id === id ? { ...excl, [key]: value } : excl))

    setFrequencyExclusions(updatedExclusions)
  }

  const getFrequencies = async () => {
    if (selectedFrequencyGroup) {
      const data = await getFrequenciesByFrequencyGroupId(selectedFrequencyGroup.id)

      if (data.data) {
        setActualNumberOfFrequencies(data.data.length)
      }
    }
  }

  const handleFrequencyBandChange = async (key: string, value: string | null | boolean | number) => {
    if (key === 'frequency_template_id') {
      const template = frequencyTemplates.find((template) => template.id === value)

      if (template && frequency) {
        setFrequency({
          ...frequency,
          start_freq: template.start_freq,
          start_indicator: template.start_indicator,
          end_freq: template.end_freq,
          end_indicator: template.end_indicator,
          frequency_template_id: Number(value),
        })
      }

      return
    }

    const updatedFrequency = frequency ? frequency : ({} as IRequestFrequencyGroup)

    setFrequency({ ...updatedFrequency, [key]: value })
  }

  useEffect(() => {
    if (frequency?.major_function_id) {
      getIntermediateFunctions()
    }
  }, [frequency?.major_function_id])

  useEffect(() => {
    if (selectedFrequencyGroup) {
      getFrequencyTemplates()
      getSingleFrequencies()
      getFrequencies()
      refreshRequestFrequencyGroupReceiveLocations()

      if (!selectedFrequencyGroup.request_location_id && requestLocations.length) {
        setFrequency({
          ...selectedFrequencyGroup,
          request_location_id: requestLocations[0].id,
        })
      } else {
        setFrequency(selectedFrequencyGroup)
      }
    }
  }, [selectedFrequencyGroup])

  const calculateAzimuthTo = (request_location_id: number | null): number => {
    if (!frequency || !request_location_id) return 0

    const sourceLocation = requestLocations.find((elem) => elem.id === frequency.request_location_id)
    const targetLocation = requestLocations.find((elem) => elem.id === request_location_id)

    if (!sourceLocation || !targetLocation) return 0

    return calculateAzimuth(
      sourceLocation.latitude_deg,
      sourceLocation.longitude_deg,
      targetLocation.latitude_deg,
      targetLocation.longitude_deg,
    )
  }

  return (
    <SectionLayout
      sectionTitle="Frequency"
      btnText="Add Frequency"
      handleAdd={handleAdd}
      isDelete={isDelete}
      handleClose={handleClose}
      handleSave={handleSave}
      handleDelete={handleDelete}
      setIsDelete={setIsDelete}
      setValue={setValue}
      deleteText={'Are you sure you want to delete this frequency?'}
      headerItems={
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box className={styles.item}>
            <Typography className={styles.label}>
              Location<span className={styles.asterisk}>*</span>:
            </Typography>
            <SelectWithSearch
              width={300}
              value={requestLocations
                .map((location) => ({
                  label: location.transmitter_location ?? '',
                  value: location.id,
                }))
                .find((elem) => elem.value === frequency?.request_location_id)}
              label="Location"
              handleChange={(value) => handleFrequencyChange('request_location_id', Number(value))}
              options={requestLocations.map((location) => ({
                value: location.id,
                label: location.transmitter_location ?? '',
              }))}
            />
          </Box>
        </Box>
      }
      isSelected={!!selectedFrequencyGroup}
      sidebarItems={
        <Box className={styles.detailedContainer}>
          {frequencyGroups.map((fg) => (
            <Box
              onClick={() => setSelectedFrequencyGroup(fg)}
              key={fg.id}
              className={
                fg.id === selectedFrequencyGroup?.id
                  ? isLightMode
                    ? styles.item
                    : styles.selectedItem
                  : isLightMode
                    ? styles.lightItem
                    : styles.item
              }
            >
              {fg.id === selectedFrequencyGroup?.id ? (
                <BookmarkIcon className={styles.circle} />
              ) : (
                <BookmarkBorderIcon className={styles.circle} />
              )}
              <Box className={styles.details}>
                <Typography>Location:</Typography>
                <Typography className={styles.info}>
                  {requestLocations.find((location) => location.id === fg.request_location_id)?.transmitter_location}
                </Typography>
              </Box>
              <Box className={styles.details}>
                <Typography>Platform:</Typography>
                <Typography className={styles.info}>{fg.platform}</Typography>
              </Box>
              <Box className={styles.details}>
                <Typography>Major Fn:</Typography>
                <Typography className={styles.info}>
                  {majorFunctions.find((elem) => elem.id === fg.major_function_id)?.name}
                </Typography>
              </Box>
              <Box className={styles.details}>
                <Typography>Detailed Fn:</Typography>
                <Typography className={styles.info}>{fg.detailed_function}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      }
      fieldItems={
        frequency
          ? [
              <Box key={'receiveLocations'} className={styles.section}>
                <Box className={styles.titleContainer}>
                  <Typography className={styles.title}>Separate Receive Locations</Typography>
                </Box>
                <Box className={styles.subSection}>
                  {receiveLocations.map((rl, i) => (
                    <Box key={i} className={styles.singleFrequency}>
                      <Typography className={styles.label}>Receive Location</Typography>
                      <SelectComponent
                        items={requestLocations.map((elem) => ({
                          value: elem.id,
                          displayValue: elem.transmitter_location,
                        }))}
                        label="Receive location"
                        value={rl.request_location_id || ''}
                        className={styles.field}
                        handleChange={(value) => handleChooseReceiveLocation(i, Number(value))}
                      />
                      <Typography className={styles.label}>Azimuth</Typography>
                      <TextField
                        className={styles.field}
                        value={calculateAzimuthTo(rl.request_location_id)}
                        InputProps={{ readOnly: true }}
                      />
                      <Button variant="outlined" onClick={() => handleDeleteReceiveLocation(i)}>
                        <DeleteIcon />
                      </Button>
                    </Box>
                  ))}
                  <Button variant="outlined" className={styles.addReceiveLocation} onClick={handleAddReceiveLocation}>
                    Add Separate Receive Location
                  </Button>
                </Box>
              </Box>,
              <Box key={'emissions'} className={styles.section}>
                <Box className={styles.titleContainer}>
                  <Typography className={styles.title}>Emissions</Typography>
                </Box>
                <Box className={styles.subSection}>
                  <Box className={styles.singleFrequency}>
                    <Typography className={styles.label}>
                      Frequency(or within frequency band)<span className={styles.asterisk}>*</span>
                    </Typography>
                    <SelectWithSearch
                      width={200}
                      isDisabled={frequency?.is_custom}
                      value={frequencyTemplates
                        .map((template) => ({
                          label: template.display,
                          value: template.id,
                        }))
                        .find((elem) => elem.value === frequency?.frequency_template_id)}
                      label="Frequency Band"
                      handleChange={(value) => handleFrequencyBandChange('frequency_template_id', Number(value))}
                      options={frequencyTemplates.map((template) => ({
                        value: template.id,
                        label: template.display,
                      }))}
                    />
                    <Box className={styles.details}>
                      <Typography>Custom</Typography>
                      <Checkbox
                        checked={Boolean(frequency?.is_custom)}
                        onChange={(_, checked) => handleFrequencyBandChange('is_custom', checked)}
                      />
                      <Select
                        className={styles.field}
                        value={frequency?.start_indicator || ''}
                        disabled={!frequency?.is_custom}
                        onChange={(evt) => handleFrequencyBandChange('start_indicator', evt.target.value)}
                      >
                        {['K', 'M', 'G', 'T'].map((measure) => (
                          <MenuItem key={measure} value={measure}>
                            {measure}
                          </MenuItem>
                        ))}
                      </Select>
                      <TextField
                        className={styles.field}
                        value={frequency?.start_freq || ''}
                        placeholder="Enter Value"
                        disabled={!frequency?.is_custom}
                        onChange={(evt) => handleFrequencyBandChange('start_freq', Number(evt.target.value))}
                      />
                      <Select
                        className={styles.field}
                        value={frequency?.end_indicator || ''}
                        disabled={!frequency?.is_custom}
                        onChange={(evt) => handleFrequencyBandChange('end_indicator', evt.target.value)}
                      >
                        {['K', 'M', 'G', 'T'].map((measure) => (
                          <MenuItem key={measure} value={measure}>
                            {measure}
                          </MenuItem>
                        ))}
                      </Select>
                      <TextField
                        value={frequency?.end_freq || ''}
                        placeholder="Enter Value"
                        className={styles.field}
                        disabled={!frequency?.is_custom}
                        onChange={(evt) => handleFrequencyBandChange('end_freq', Number(evt.target.value))}
                      />
                    </Box>
                  </Box>
                </Box>
                <Box className={styles.subSection}>
                  {frequencyExclusions.map((excl, index) => (
                    <Box className={styles.singleFrequency} key={excl.id}>
                      <Typography>{index + 1}.</Typography>
                      <Box className={styles.details}>
                        <Select sx={{ display: 'none' }}>
                          {frequencyTemplates.map((template) => (
                            <MenuItem key={template.id} value={template.id}>
                              {template.display}
                            </MenuItem>
                          ))}
                        </Select>
                        <Typography>Band</Typography>
                        <Checkbox
                          checked={Boolean(excl.is_band)}
                          onChange={(_, checked) => handleFrequencyExclusionUpdate(excl.id, 'is_band', checked)}
                        />
                        <Select
                          className={styles.field}
                          defaultValue="K"
                          value={excl.start_indicator || ''}
                          onChange={(evt) =>
                            handleFrequencyExclusionUpdate(excl.id, 'start_indicator', evt.target.value)
                          }
                        >
                          {['K', 'M', 'G', 'T'].map((measure) => (
                            <MenuItem key={measure} value={measure}>
                              {measure}
                            </MenuItem>
                          ))}
                        </Select>
                        <TextField
                          className={styles.field}
                          value={excl.start_freq || ''}
                          placeholder="Enter Value"
                          onChange={(evt) =>
                            handleFrequencyExclusionUpdate(excl.id, 'start_freq', Number(evt.target.value))
                          }
                        />
                        <Select
                          className={styles.field}
                          defaultValue="K"
                          value={excl.end_indicator || ''}
                          onChange={(evt) => handleFrequencyExclusionUpdate(excl.id, 'end_indicator', evt.target.value)}
                        >
                          {['K', 'M', 'G', 'T'].map((measure) => (
                            <MenuItem key={measure} value={measure}>
                              {measure}
                            </MenuItem>
                          ))}
                        </Select>
                        <TextField
                          value={excl.end_freq || ''}
                          placeholder="Enter Value"
                          className={styles.field}
                          onChange={(evt) =>
                            handleFrequencyExclusionUpdate(excl.id, 'end_freq', Number(evt.target.value))
                          }
                        />
                        <Button color="error" variant="outlined" onClick={() => handleSingleFrequencyDelete(excl.id)}>
                          <DeleteIcon />
                        </Button>
                      </Box>
                    </Box>
                  ))}
                  <Button variant="outlined" className={styles.addFreq} onClick={handleAddSingleFrequency}>
                    Add Excluded Freq
                  </Button>
                </Box>
              </Box>,
              <Box key={'frequencyCharacteristics'} className={styles.section}>
                <Box className={styles.titleContainer}>
                  <Typography className={styles.title}>Frequency Characteristics</Typography>
                </Box>
                <Box className={styles.subSection}>
                  <Box className={styles.fieldContainer}>
                    <Typography className={styles.label}>
                      Platform<span className={styles.asterisk}>*</span>:
                    </Typography>
                    <TextField
                      value={frequency?.platform || ''}
                      label="Platform"
                      className={styles.field}
                      onChange={(evt) => handleFrequencyChange('platform', evt.target.value)}
                    />
                  </Box>
                  <Box className={styles.fieldContainer}>
                    <Typography className={styles.label}>Number of Frequencies:</Typography>
                    <TextField
                      value={frequency?.number_of_frequencies || ''}
                      label="Number of Frequencies"
                      type="number"
                      inputProps={{ min: actualNumberOfFrequencies }}
                      className={styles.field}
                      onChange={(evt) => handleFrequencyChange('number_of_frequencies', Number(evt.target.value))}
                    />
                  </Box>
                  <Box className={styles.fieldContainer}>
                    <Typography className={styles.label}>
                      Major function<span className={styles.asterisk}>*</span>:
                    </Typography>
                    <SelectComponent
                      items={majorFunctions.map((elem) => ({
                        value: elem.id,
                        displayValue: elem.name,
                      }))}
                      label="Major FN"
                      value={frequency?.major_function_id || ''}
                      className={styles.field}
                      handleChange={(value) => handleFrequencyChange('major_function_id', Number(value))}
                    />
                  </Box>
                  <Box className={styles.fieldContainer}>
                    <Typography className={styles.label}>
                      Intermediate Function<span className={styles.asterisk}>*</span>:
                    </Typography>
                    <SelectComponent
                      items={intermediateFunctions.map((elem) => ({
                        value: elem.id,
                        displayValue: elem.name,
                      }))}
                      label="Intermediate FN"
                      value={frequency?.intermediate_function_id || ''}
                      className={styles.field}
                      handleChange={(value) => handleFrequencyChange('intermediate_function_id', Number(value))}
                    />
                  </Box>
                  <Box className={styles.fieldContainer}>
                    <Typography className={styles.label}>Detailed Function:</Typography>
                    <TextField
                      value={frequency?.detailed_function || ''}
                      label="Detailed FN"
                      className={styles.field}
                      onChange={(evt) => handleFrequencyChange('detailed_function', evt.target.value)}
                    />
                  </Box>
                  <Box className={styles.fieldContainer}>
                    <Typography className={styles.label}>Daily Utilization:</Typography>
                    <SelectComponent
                      items={usageTimes.map((elem) => ({
                        value: elem.id,
                        displayValue: elem.name,
                      }))}
                      label="Daily Utilization"
                      value={frequency?.usage_time_id || ''}
                      className={styles.field}
                      handleChange={(value) => handleFrequencyChange('usage_time_id', Number(value))}
                    />
                  </Box>
                  <Box className={styles.fieldContainer}>
                    <Typography className={styles.label}>
                      Usage Time Service<span className={styles.asterisk}>*</span>:
                    </Typography>
                    <SelectComponent
                      label="Usage Time Service"
                      value={frequency?.usage_time_service_id || ''}
                      className={styles.field}
                      maxWidth={600}
                      handleChange={(value) => handleFrequencyChange('usage_time_service_id', Number(value))}
                      items={usageTimeServices.map((elem) => ({
                        value: elem.id,
                        displayValue: elem.name,
                      }))}
                    />
                  </Box>
                  <TextField
                    fullWidth
                    label="Additional Remarks"
                    value={frequency?.supplementary_details || ''}
                    onChange={(evt) => handleFrequencyChange('supplementary_details', evt.target.value)}
                    type="area"
                  />
                </Box>
              </Box>,
            ]
          : []
      }
    />
  )
}

export default FrequencySection
